import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  MenuItem,
} from "@mui/material";

const API = process.env.REACT_APP_API_URL;

// Library-only URLs
const BILL_URL = `${API}library/generate-next-month-invoice/`;
const INVOICE_URL = `${API}library/my-invoices/`;
const PDF_URL = (id) => `${API}library/invoices/${id}/download-pdf/`;

const StudentBill = () => {
  const [selectedBillType, setSelectedBillType] = useState("");
  const [loading, setLoading] = useState(false);

  // Only library invoices now
  const [libraryInvoices, setLibraryInvoices] = useState([]);
  const [filters, setFilters] = useState({ is_paid: "", month: "", year: "" });
  const [invoiceModal, setInvoiceModal] = useState({
    open: false,
    invoice: null,
  });

  // --- helpers for dates ---
  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value; // backend agar string de to raw show
    return d.toLocaleDateString("en-GB");
  };

  // backend se aa sakne wale alag–alag field names ko handle karo
  const getBookingEnd = (inv) =>
    inv.booking_end_date ||
    inv.period_end ||
    inv.expiry_date ||
    inv.end_date ||
    null;

  // Fetch library invoices
  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const params = new URLSearchParams();
      if (filters.is_paid) params.append("is_paid", filters.is_paid);
      if (filters.month) params.append("month", filters.month);
      if (filters.year) params.append("year", filters.year);

      const res = await axios.get(
        `${INVOICE_URL}?${params.toString()}`,
        config
      );

      setLibraryInvoices(
        [...res.data].sort((a, b) => {
          if (a.generated_on && b.generated_on) {
            return new Date(b.generated_on) - new Date(a.generated_on);
          }
          return (b.id || 0) - (a.id || 0);
        })
      );
    } catch (err) {
      toast.error("Failed to fetch invoices");
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line
  }, [filters]);

  // Generate Library invoice
  const handleGenerateBill = async () => {
    if (!selectedBillType) {
      toast.error("Please select bill type");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(
        BILL_URL,
        {
          student_id: localStorage.getItem("student_id"),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Library bill generated 🎉");
      setInvoiceModal({ open: true, invoice: res.data });
      fetchInvoices();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          "Error generating bill"
      );
    } finally {
      setLoading(false);
    }
  };

  // Download Library invoice PDF
  const handleDownloadPDF = async (invoiceId) => {
    const loadingToast = toast.loading("Downloading invoice...");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(PDF_URL(invoiceId), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `library_invoice_${invoiceId}.pdf`;
      link.click();
      toast.dismiss(loadingToast);
      toast.success("✅ Invoice downloaded successfully.");
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to download invoice.");
    }
  };

  // Filter helpers
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - 2 + i
  );
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        {/* Generate Bill Card */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl">
              <ReceiptOutlinedIcon className="text-white text-2xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Generate Monthly Bill
              </h2>
              <p className="text-gray-600 text-sm">
                Create invoices for library services
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <select
                value={selectedBillType}
                onChange={(e) => setSelectedBillType(e.target.value)}
                className={
                  "w-full sm:w-auto px-6 py-3 rounded-xl font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-yellow-400 text-black hover:bg-yellow-300 border-2 border-yellow-400"
                }
              >
                <option value="">🧾 Generate Bill</option>
                <option value="library">📚 Library Seat</option>
              </select>

              <Button
                onClick={handleGenerateBill}
                variant="contained"
                className={`w-full sm:w-auto px-6 py-3 rounded-xl font-semibold transition-colors duration-200 bg-blue-600 text-white hover:bg-blue-700`}
                sx={{ borderRadius: "12px" }}
              >
                {loading ? "Generating..." : "Generate"}
              </Button>
            </div>

            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">
                ⚠️ Bill can only be generated in the last 3 days of current
                cycle.
              </p>
            </div>
          </div>
        </div>

        {/* Invoice List Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-800">
              Library Invoices
            </h3>
            <button
              onClick={fetchInvoices}
              className="flex items-center gap-2 bg-gray-100 text-gray-600 p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ml-2"
              title="Refresh invoices"
            >
              <RefreshIcon
                className={`text-sm ${loading ? "animate-spin" : ""}`}
              />
            </button>
          </div>

          {/* Filters */}
          <Box className="flex flex-wrap gap-3 mt-4 mb-4">
            <TextField
              select
              label="Paid Status"
              value={filters.is_paid}
              onChange={(e) =>
                setFilters((f) => ({ ...f, is_paid: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Paid</MenuItem>
              <MenuItem value="false">Unpaid</MenuItem>
            </TextField>
            <TextField
              select
              label="Month"
              value={filters.month}
              onChange={(e) =>
                setFilters((f) => ({ ...f, month: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              {monthNames.map((name, idx) => (
                <MenuItem
                  key={name}
                  value={(idx + 1).toString().padStart(2, "0")}
                >
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Year"
              value={filters.year}
              onChange={(e) =>
                setFilters((f) => ({ ...f, year: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">All</MenuItem>
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* ===== Desktop Table ===== */}
          <Box className="hidden md:block overflow-x-auto mt-4">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Invoice ID
                  </th>
                  {/* Month ko booking start ke tarah treat kar rahe hain */}
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Booking Start
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Amount
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Deposit
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Total
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Status
                  </th>
                  {/* Booking End Date */}
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Booking End
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Generated On
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {libraryInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <ReceiptOutlinedIcon className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No invoices found
                        </p>
                        <p className="text-gray-400 text-sm">
                          Try adjusting your filters or refresh the page
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  libraryInvoices.map((inv, idx) => {
                    const endRaw = getBookingEnd(inv);
                    return (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50 transition-colors duration-200"
                      >
                        <td className="px-4 py-4 font-mono text-gray-600">
                          {inv.invoice_id}
                        </td>
                        <td className="px-4 py-4 text-gray-600">
                          {formatDate(inv.month)}
                        </td>
                        <td className="px-4 py-4 text-gray-600">
                          ₹{inv.amount}
                        </td>
                        <td className="px-4 py-4 text-gray-600">
                          ₹{inv.deposit}
                        </td>
                        <td className="px-4 py-4 font-bold text-gray-800">
                          ₹{inv.total}
                        </td>
                        <td className="px-4 py-4">
                          {inv.is_paid ? (
                            <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                              Paid
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                              Unpaid
                            </span>
                          )}
                        </td>
                        {/* Booking End Date cell */}
                        <td className="px-4 py-4 text-gray-600">
                          {endRaw ? formatDate(endRaw) : "-"}
                        </td>
                        <td className="px-4 py-4 text-gray-600 max-w-xs truncate">
                          {new Date(inv.generated_on).toLocaleString()}
                        </td>
                        <td className="px-4 py-4">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleDownloadPDF(inv.id)}
                            className="rounded-lg border-blue-500 text-blue-600 hover:bg-blue-50"
                            startIcon={<ReceiptOutlinedIcon />}
                          >
                            Download PDF
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>

          {/* ===== Mobile Cards ===== */}
          <Box className="grid grid-cols-1 gap-6 mt-4 md:hidden">
            {libraryInvoices.length === 0 ? (
              <div className="col-span-full text-center py-6 text-gray-500 bg-gray-50 rounded-xl shadow-inner">
                No invoices found.
              </div>
            ) : (
              libraryInvoices.map((inv, idx) => {
                const endRaw = getBookingEnd(inv);
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-xl shadow-md p-5 flex flex-col gap-3 border border-gray-100 hover:shadow-lg transition-shadow duration-200"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <ReceiptOutlinedIcon className="text-yellow-500 text-2xl" />
                      <div>
                        <div className="font-bold text-lg text-gray-800">
                          {inv.invoice_id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {inv.organisation}
                        </div>
                      </div>
                      <div className="ml-auto">
                        {inv.is_paid ? (
                          <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                            Paid
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                            Unpaid
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-700">
                      <div className="flex-1 min-w-[120px]">
                        <span className="font-medium">Booking Start:</span>{" "}
                        {formatDate(inv.month)}
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <span className="font-medium">Amount:</span> ₹
                        {inv.amount}
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <span className="font-medium">Deposit:</span> ₹
                        {inv.deposit}
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <span className="font-medium">Total:</span>{" "}
                        <span className="font-bold">₹{inv.total}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                      <div>
                        Generated:{" "}
                        {new Date(inv.generated_on).toLocaleString()}
                      </div>
                      {endRaw && (
                        <div>
                          End: {formatDate(endRaw)}
                        </div>
                      )}
                      {inv.seat && <div>Seat: {inv.seat}</div>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDownloadPDF(inv.id)}
                        className="rounded-lg border-blue-500 text-blue-600 hover:bg-blue-50"
                        startIcon={<ReceiptOutlinedIcon />}
                      >
                        Download PDF
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </Box>
        </div>
      </div>

      {/* Invoice Details Modal */}
      <Dialog
        open={invoiceModal.open}
        onClose={() => setInvoiceModal({ open: false, invoice: null })}
      >
        <DialogTitle>Invoice Generated</DialogTitle>
        <DialogContent>
          {invoiceModal.invoice && (
            <Box className="space-y-2 p-2 md:p-4">
              <div className="flex items-center gap-3 mb-2">
                <ReceiptOutlinedIcon className="text-yellow-500 text-2xl" />
                <div>
                  <div className="font-bold text-lg text-gray-800">
                    {invoiceModal.invoice.invoice_id}
                  </div>
                  <div className="text-xs text-gray-500">
                    {invoiceModal.invoice.organisation}
                  </div>
                </div>
                <div className="ml-auto">
                  {invoiceModal.invoice.is_paid ? (
                    <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                      Paid
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                      Unpaid
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-sm text-gray-700">
                <div className="flex-1 min-w-[120px]">
                  <span className="font-medium">Invoice Start Month:</span>{" "}
                  {formatDate(invoiceModal.invoice.month)}
                </div>

                {/* Booking End Date in modal */}
                {getBookingEnd(invoiceModal.invoice) && (
                  <div className="flex-1 min-w-[120px]">
                    <span className="font-medium">Booking End Date:</span>{" "}
                    {formatDate(getBookingEnd(invoiceModal.invoice))}
                  </div>
                )}

                <div className="flex-1 min-w-[120px]">
                  <span className="font-medium">Amount:</span> ₹
                  {invoiceModal.invoice.amount}
                </div>
                <div className="flex-1 min-w-[120px]">
                  <span className="font-medium">Deposit:</span> ₹
                  {invoiceModal.invoice.deposit}
                </div>
                <div className="flex-1 min-w-[120px]">
                  <span className="font-medium">Total:</span>{" "}
                  <span className="font-bold">
                    ₹{invoiceModal.invoice.total}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                <div>
                  Generated:{" "}
                  {new Date(
                    invoiceModal.invoice.generated_on
                  ).toLocaleString()}
                </div>
                {invoiceModal.invoice.seat && (
                  <div>Seat: {invoiceModal.invoice.seat}</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    handleDownloadPDF(invoiceModal.invoice.id)
                  }
                  className="rounded-lg border-blue-500 text-blue-600 hover:bg-blue-50"
                  startIcon={<ReceiptOutlinedIcon />}
                >
                  Download PDF
                </Button>
              </div>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInvoiceModal({ open: false, invoice: null })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StudentBill;
